"use client";

import clsx from "clsx";
import { Loader2 } from "lucide-react";
import { Control, Controller, Path } from "react-hook-form";
import Select, { ControlProps, GroupBase, components } from "react-select";

type SelectControlProps<T> = ControlProps<
  {
    value: T;
    label: string;
  },
  false,
  GroupBase<{ value: T; label: string }>
>;

// Custom loading indicator component
const CustomLoadingIndicator = () => {
  return (
    <div className="flex items-center justify-center px-2">
      <Loader2 className="h-4 w-4 animate-spin text-primary" />
    </div>
  );
};

const CustomNoOptionsMessage = ({ children, ...props }: any) => {
  return (
    <components.NoOptionsMessage {...props}>
      <div className="custom-no-options text-secondary-foreground">{children}</div>
    </components.NoOptionsMessage>
  );
};

interface FormSelectFieldProps<T extends Record<string, any>> {
  name: Path<T>;
  control: Control<T>;
  options: Array<{ value: any; label: string }>;
  label?: string;
  helperText?: string;
  isLoading?: boolean;
  isDisabled?: boolean;
  isMulti?: boolean;
  required?: boolean;
  tabIndex?: number;
  afterSelectCallback?: () => void;
  customNoOptionsMessage?: () => string;
  customClassNames?: {
    control?: (state: SelectControlProps<any>) => string;
    valueContainer?: () => string;
    placeholder?: () => string;
    input?: () => string;
    menu?: () => string;
    menuList?: () => string;
    option?: (state: { isSelected: boolean; isFocused: boolean }) => string;
    singleValue?: () => string;
    indicatorsContainer?: () => string;
    dropdownIndicator?: (state: { selectProps: { menuIsOpen: boolean } }) => string;
    clearIndicator?: () => string;
    indicatorSeparator?: () => string;
    multiValue?: () => string;
    multiValueLabel?: () => string;
    multiValueRemove?: () => string;
    noOptionsMessage?: () => string;
  };
}

export default function FormSelectField<T extends Record<string, any>>({
  name,
  control,
  options,
  label,
  helperText,
  isLoading = false,
  isDisabled = false,
  isMulti = false,
  required = false,
  tabIndex,
  customNoOptionsMessage,
  afterSelectCallback,
  customClassNames = {},
}: FormSelectFieldProps<T>) {
  const defaultClassNames = {
    control: (state: SelectControlProps<any>) =>
      clsx(
        "flex items-center bg-secondary-light border border-secondary-light rounded-2xl min-h-[42px] hover:cursor-pointer hover:border-grey-border p-3 transition-all duration-300",
        state.isFocused ? "border-grey-border ring-1 ring-grey-border" : "",
        state.isDisabled && "opacity-50",
      ),
    valueContainer: () => "flex flex-wrap flex-1 px-3 py-1 gap-1",
    placeholder: () => "inter text-secondary-foreground text-medium",
    input: () => "text-secondary-foreground font-medium !text-xs inter focus:outline-none p-0 m-0",
    menu: () => "mt-1 mb-2 border border-secondary-light rounded-2xl bg-white shadow-lg absolute w-full z-50",
    menuList: () => "max-h-60 inter font-medium overflow-auto no-scrollbar",
    option: ({ isSelected, isFocused }: { isSelected: boolean; isFocused: boolean }) =>
      clsx(
        "px-4 py-2 cursor-pointer inter font-medium first:rounded-t-2xl last:rounded-b-2xl",
        isSelected && "bg-secondary-light text-black",
        !isSelected && isFocused && "bg-secondary-light text-black overflow-hidden",
        !isSelected && !isFocused && "text-gray-800 hover:bg-gray-50",
      ),
    singleValue: () => "text-[#000000] inter font-medium",
    indicatorsContainer: () => "flex items-center px-2",
    dropdownIndicator: (state: { selectProps: { menuIsOpen: boolean } }) =>
      clsx(
        "!text-gray-400 !hover:text-gray-600",
        "!transition-all !duration-300 !ease-in-out",
        state.selectProps.menuIsOpen ? "!rotate-180" : "!rotate-0",
      ),
    clearIndicator: () => "text-primary p-1",
    indicatorSeparator: () => "hidden",
    multiValue: () => "bg-white text-white p-2 rounded-lg",
    multiValueLabel: () => "text-black inter font-semibold",
    multiValueRemove: () => "text-primary ms-2",
    noOptionsMessage: () => "text-secondary-foreground p-4",
  };

  const classNames = Object.fromEntries(
    Object.entries(defaultClassNames).map(([key, defaultFn]) => [
      key,
      customClassNames[key as keyof typeof customClassNames]
        ? (state: any) => clsx(defaultFn(state), customClassNames[key as keyof typeof customClassNames]?.(state))
        : defaultFn,
    ]),
  );

  return (
    <Controller
      name={name}
      control={control}
      rules={{ required: required ? "This field is required" : false }}
      render={({ field: { onChange, value, ...field }, fieldState: { error } }) => (
        <div className="w-full">
          {label && (
            <label className="inter mb-2 block text-sm font-semibold text-[#000000]">
              {label}
              {required && <span className="ml-1 text-red-500">*</span>}
            </label>
          )}
          <Select
            {...field}
            tabIndex={tabIndex}
            unstyled
            instanceId={name}
            placeholder={helperText}
            options={options}
            value={
              value
                ? isMulti
                  ? options.filter((opt) => value.includes(opt.value))
                  : options.find((opt) => opt.value === value)
                : null
            }
            onChange={(option) => {
              if (isMulti) {
                const selectedOptions = option as Array<{ value: any; label: string }>;
                onChange(selectedOptions?.map((opt) => opt.value) || []);
              } else {
                const selectedOption = option as { value: any; label: string } | null;
                onChange(selectedOption?.value ?? null);
              }

              afterSelectCallback?.();
            }}
            classNames={classNames}
            isClearable
            isLoading={isLoading}
            isDisabled={isDisabled}
            isMulti={isMulti}
            components={{
              ...components,
              LoadingIndicator: CustomLoadingIndicator,
              NoOptionsMessage: CustomNoOptionsMessage,
            }}
            noOptionsMessage={() => customNoOptionsMessage?.() || "No options available"}
            closeMenuOnSelect={!isMulti}
          />
          {error && <div className="inter mt-1 text-sm text-red-500">{error.message}</div>}
        </div>
      )}
    />
  );
}
